import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Article, Refresh, Search } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import PageTitle from "../components/PageTitle";
import { mediBlogService, MediBlogArticle, MediBlogCategory } from "../services/mediBlog";
import { getLocalizedArticleField, getLocalizedCategoryName } from "../utils/mediBlogLocale";
import { useSeoMeta } from "../utils/seoMeta";

const MediBlog: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [articles, setArticles] = useState<MediBlogArticle[]>([]);
  const [categories, setCategories] = useState<MediBlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  useSeoMeta({
    title: t("mediblog.seo.listTitle"),
    description: t("mediblog.seo.listDescription"),
    keywords: t("mediblog.seo.listKeywords"),
  });

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [articlesRes, categoriesRes] = await Promise.all([
        mediBlogService.getArticles({ publishedOnly: true }),
        mediBlogService.getCategories(true),
      ]);
      setArticles(articlesRes.data);
      setCategories(categoriesRes.data);
    } catch {
      setError(t("mediblog.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchData();
  }, [t]);

  const filteredArticles = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return articles.filter((article) => {
      const title = getLocalizedArticleField(article, "title", i18n.language).toLowerCase();
      const excerpt = getLocalizedArticleField(article, "excerpt", i18n.language).toLowerCase();
      const matchesSearch = !q || title.includes(q) || excerpt.includes(q);
      const matchesCategory =
        categoryFilter === "all" ||
        article.category?.slug === categoryFilter ||
        categories.find((c) => c.id === article.category_id)?.slug === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [articles, categories, categoryFilter, i18n.language, searchQuery]);

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8, minHeight: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <PageTitle text={t("mediblog.title")} size="medium" align="center" />
      <Typography
        variant="body1"
        color="text.secondary"
        align="center"
        sx={{ mt: -2, mb: 4, maxWidth: 640, mx: "auto" }}
      >
        {t("mediblog.subtitle")}
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 4 }}
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder={t("mediblog.searchPlaceholder")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label={t("mediblog.categoryFilter")}
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          sx={{ minWidth: { sm: 220 } }}
        >
          <MenuItem value="all">{t("mediblog.allCategories")}</MenuItem>
          {categories.map((cat) => (
            <MenuItem key={cat.id} value={cat.slug}>
              {getLocalizedCategoryName(cat, i18n.language)}
            </MenuItem>
          ))}
        </TextField>
        <Button startIcon={<Refresh />} onClick={() => void fetchData()} variant="outlined" size="small">
          {t("common.refresh", { defaultValue: "Refresh" })}
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {filteredArticles.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            px: 3,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, isDark ? 0.08 : 0.04),
          }}
        >
          <Article sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {t("mediblog.noArticles")}
          </Typography>
          <Typography color="text.secondary">{t("mediblog.noArticlesHint")}</Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredArticles.map((article) => {
            const title = getLocalizedArticleField(article, "title", i18n.language);
            const excerpt = getLocalizedArticleField(article, "excerpt", i18n.language);
            const category = article.category ?? categories.find((c) => c.id === article.category_id);

            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={article.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                    },
                  }}
                >
                  <CardActionArea
                    component={RouterLink}
                    to={`/mediblog/${article.slug}`}
                    sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "stretch" }}
                  >
                    {article.featured_image ? (
                      <CardMedia
                        component="img"
                        height="180"
                        image={article.featured_image}
                        alt={title}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 180,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                        }}
                      >
                        <Article sx={{ fontSize: 48, color: "primary.main", opacity: 0.5 }} />
                      </Box>
                    )}
                    <CardContent sx={{ flex: 1 }}>
                      {category && (
                        <Chip
                          label={getLocalizedCategoryName(category, i18n.language)}
                          size="small"
                          sx={{ mb: 1 }}
                        />
                      )}
                      <Typography variant="h6" component="h2" gutterBottom sx={{ fontSize: "1.1rem" }}>
                        {title}
                      </Typography>
                      {excerpt && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {excerpt.slice(0, 140)}
                          {excerpt.length > 140 ? "…" : ""}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {article.author_name && `${article.author_name} · `}
                        {formatDate(article.published_at)}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Container>
  );
};

export default MediBlog;
